import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Flag, X, Send, CheckCircle2, Loader2, AlertTriangle, Camera, ImagePlus, Trash2, Pencil, Undo2, Eraser, Highlighter, ArrowUpRight, Zap } from "lucide-react";
import { API, useApp } from "../context";
import { useDraggableFab } from "../lib/useDraggableFab";
import { useFabsCollapsed } from "../lib/fabTidy";

// Map a pathname to a friendly "area" so the report is pre-filled with where the
// user was when something broke — they can edit it before sending.
function detectArea(pathname) {
  const p = (pathname || "").toLowerCase();
  const rules = [
    ["/learn/literacy", "Literacy Corner"],
    ["/learn/curriculum", "My Curriculum"],
    ["/learn/tutor", "Professor Owl Tutor"],
    ["/learn/upload", "Upload & Learn"],
    ["/learn", "Learning Academy"],
    ["/arcade", "Arcade"],
    ["/adventures", "Adventures"],
    ["/create", "Create"],
    ["/parent", "Parent Corner"],
    ["/dashboard", "Dashboard"],
    ["/shop", "Shop"],
    ["/marketplace", "Marketplace"],
    ["/profile", "Profile"],
    ["/contact", "Contact Page"],
  ];
  for (const [prefix, label] of rules) {
    if (p.startsWith(prefix)) return label;
  }
  if (p === "/" || p === "") return "Home";
  return "Isaiah's World";
}

const IMAGE_EXT_RE = /\.(png|jpe?g|webp|gif|svg|bmp|heic|heif)$/i;

export default function ReportProblem() {
  const location = useLocation();
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  // "Report a Problem" is one consistent right-edge pop-out tab on EVERY page.
  // Its × tucks it away (out of the way of games / the Owl chat); the green
  // magnet (lower-left) untucks it and restacks every other helper too.
  const [tucked, setTucked] = useState(() => { try { return localStorage.getItem("iw_report_hidden") === "1"; } catch { return false; } });
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 1279px)").matches);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 1279px)");
    const h = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", h); } catch { mq.addListener(h); }
    return () => { try { mq.removeEventListener("change", h); } catch { mq.removeListener(h); } };
  }, []);
  const tuckReport = () => { try { localStorage.setItem("iw_report_hidden", "1"); } catch { /* quota */ } setTucked(true); };
  // The green magnet broadcasts "iw-fabs-tidy" — bring the tab back with it.
  useEffect(() => {
    const onTidy = () => { try { localStorage.removeItem("iw_report_hidden"); } catch { /* quota */ } setTucked(false); };
    window.addEventListener("iw-fabs-tidy", onTidy);
    return () => window.removeEventListener("iw-fabs-tidy", onTidy);
  }, []);
  // Only mobile can tuck it away (that's where it gets in the way and where the
  // restore magnet lives); on desktop the tab always stays put.
  const canTuck = isMobile;
  // Draggable like the other helpers — move it up/down or to either edge.
  // Default: right edge, roughly vertically centered (its original spot).
  const reportDrag = useDraggableFab("iw_fab_pos_report", 360, { defaultSide: "right", edge: 0 });
  // It also belongs to the "Show Helpers" group, so the tidy toggle hides/shows
  // it along with the gift, command-center and the magnet.
  const fabsCollapsed = useFabsCollapsed();
  const [area, setArea] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [attachments, setAttachments] = useState([]); // [{url, path, label, uploading}]
  const [attachError, setAttachError] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [shotUrl, setShotUrl] = useState("");   // captured screenshot dataURL (annotated on submit)
  const [drewOnShot, setDrewOnShot] = useState(false);
  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const bgImgRef = useRef(null);
  const drawingRef = useRef(false);
  const startPtRef = useRef(null);
  const snapRef = useRef(null); // committed pixels (bg + finished strokes)
  const undoRef = useRef([]);   // stack of committed states for step-by-step undo
  const [canUndo, setCanUndo] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [tool, setTool] = useState("pen"); // pen | highlighter | arrow

  // Refresh the auto-detected area + email each time the panel opens.
  useEffect(() => {
    if (open) {
      setArea(detectArea(location.pathname));
      setEmail(user?.email || "");
      setState("idle");
      setMessage("");
      setAttachments([]);
      setAttachError("");
      setBlocking(false);
    }
    // NOTE: do NOT reset shotUrl here — openWithCapture sets it right before
    // opening, and this effect fires AFTER open flips true (it used to wipe the
    // freshly-captured screenshot, which is why the preview never showed).
  }, [open, location.pathname, user]);

  // Auto-capture the current screen the MOMENT the button is clicked — BEFORE the
  // modal opens — so the report shows exactly what the user was looking at, not
  // the report popup itself. (Opening first then snapping captured the modal.)
  const openWithCapture = async () => {
    setCapturing(true);
    // reset any previous shot + drawing state before we capture a fresh one
    setShotUrl("");
    setDrewOnShot(false);
    setCanUndo(false);
    undoRef.current = [];
    bgImgRef.current = null;
    snapRef.current = null;
    setOpen(true);      // open IMMEDIATELY so a single tap responds — the modal
                        // shows a "capturing…" state while we snap in the
                        // background (the snapshot excludes the modal itself).
    let dataUrl = "";
    // Primary: SnapDOM — renders via the browser's own engine (foreignObject), so
    // it handles modern CSS (oklch/color-mix/CSS vars) that html2canvas chokes on.
    // This is why the desktop preview used to come up blank.
    try {
      const { snapdom } = await import("@zumer/snapdom");
      await new Promise((r) => requestAnimationFrame(() => r()));
      const canvas = await snapdom.toCanvas(document.body, {
        backgroundColor: "#0f172a",
        scale: Math.min(1, (window.devicePixelRatio || 1)) * 0.7,
        embedFonts: false,
        fast: true,
        exclude: ["[data-testid='report-problem-button']", "[data-testid='report-problem-modal']"],
      });
      dataUrl = canvas.toDataURL("image/png");
    } catch {
      dataUrl = "";
    }
    // Fallback: html2canvas (older raster path) if SnapDOM ever fails.
    if (!dataUrl) {
      try {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(document.body, {
          logging: false, useCORS: true, allowTaint: false, backgroundColor: "#0f172a", scale: 0.6,
          windowWidth: document.documentElement.clientWidth,
          windowHeight: document.documentElement.clientHeight,
          x: window.scrollX, y: window.scrollY,
          width: document.documentElement.clientWidth,
          height: document.documentElement.clientHeight,
          ignoreElements: (el) => el?.getAttribute?.("data-testid") === "report-problem-button"
            || el?.getAttribute?.("data-testid") === "report-problem-modal",
        });
        dataUrl = canvas.toDataURL("image/png");
      } catch {
        dataUrl = "";
      }
    }
    setShotUrl(dataUrl);
    setCapturing(false);
  };

  // Paint the captured screenshot into the annotate canvas so the user can draw on it.
  useEffect(() => {
    if (!shotUrl || !canvasRef.current) return;
    const img = new Image();
    img.onload = () => {
      bgImgRef.current = img;
      const c = canvasRef.current;
      if (!c) return;
      c.width = img.width; c.height = img.height; // native res for drawing quality
      // Responsive display: fill the container width, keep aspect ratio (never a
      // fixed px width — that overflowed narrow phone screens and clipped the popup).
      c.style.width = "100%"; c.style.height = "auto"; c.style.display = "block";
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0);
      snapRef.current = ctx.getImageData(0, 0, c.width, c.height);
      undoRef.current = [snapRef.current]; // base state
      setCanUndo(false);
    };
    img.src = shotUrl;
  }, [shotUrl]);

  const canvasPoint = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  };
  const _applyStyle = (ctx, w) => {
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (tool === "highlighter") {
      ctx.strokeStyle = "rgba(255,214,10,0.42)"; ctx.lineWidth = Math.max(16, w / 26);
    } else if (tool === "arrow") {
      ctx.strokeStyle = "#FF2D55"; ctx.lineWidth = Math.max(5, w / 100); ctx.fillStyle = "#FF2D55";
    } else {
      ctx.strokeStyle = "#FF2D55"; ctx.lineWidth = Math.max(4, w / 120);
    }
  };
  const _drawArrow = (ctx, a, b) => {
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    const head = Math.max(14, canvasRef.current.width / 40);
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 7), b.y - head * Math.sin(ang - Math.PI / 7));
    ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 7), b.y - head * Math.sin(ang + Math.PI / 7));
    ctx.closePath(); ctx.fill();
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = canvasPoint(e);
    startPtRef.current = p;
    _applyStyle(ctx, c.width);
    if (tool !== "arrow") { ctx.beginPath(); ctx.moveTo(p.x, p.y); }
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = canvasPoint(e);
    if (tool === "arrow") {
      if (snapRef.current) ctx.putImageData(snapRef.current, 0, 0); // live preview
      _applyStyle(ctx, c.width);
      _drawArrow(ctx, startPtRef.current, p);
    } else {
      ctx.lineTo(p.x, p.y); ctx.stroke();
    }
    if (!drewOnShot) setDrewOnShot(true);
  };
  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const c = canvasRef.current;
    if (c) {
      snapRef.current = c.getContext("2d").getImageData(0, 0, c.width, c.height); // commit
      undoRef.current.push(snapRef.current);
      setCanUndo(undoRef.current.length > 1);
    }
  };
  const undoDraw = () => {
    const c = canvasRef.current;
    if (!c || undoRef.current.length <= 1) return;
    undoRef.current.pop();
    const prev = undoRef.current[undoRef.current.length - 1];
    c.getContext("2d").putImageData(prev, 0, 0);
    snapRef.current = prev;
    setCanUndo(undoRef.current.length > 1);
    setDrewOnShot(undoRef.current.length > 1);
  };
  const clearDrawing = () => {
    const c = canvasRef.current;
    if (!c || !bgImgRef.current) return;
    const ctx = c.getContext("2d");
    ctx.drawImage(bgImgRef.current, 0, 0);
    snapRef.current = ctx.getImageData(0, 0, c.width, c.height);
    undoRef.current = [snapRef.current];
    setCanUndo(false);
    setDrewOnShot(false);
  };
  const removeShot = () => { setShotUrl(""); setDrewOnShot(false); setCanUndo(false); bgImgRef.current = null; snapRef.current = null; undoRef.current = []; };

  const uploadBlob = async (blob, filename, label) => {
    const tmpId = Math.random().toString(36).slice(2);
    setAttachments((a) => [...a, { id: tmpId, label, uploading: true }]);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const r = await axios.post(`${API}/forms/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => a.map((x) => x.id === tmpId ? { ...x, uploading: false, url: r.data?.url, path: r.data?.path } : x));
      return true;
    } catch (e) {
      setAttachments((a) => a.filter((x) => x.id !== tmpId));
      setAttachError(e?.response?.data?.detail || "Couldn't attach that picture — please try again.");
      return false;
    }
  };

  // Flatten the annotated screenshot canvas to a blob and upload it; returns its path.
  const uploadShot = async () => {
    const c = canvasRef.current;
    if (!shotUrl || !c) return null;
    const blob = await new Promise((res) => c.toBlob(res, "image/png", 0.9));
    if (!blob) return null;
    try {
      const fd = new FormData();
      fd.append("file", blob, "screenshot.png");
      const r = await axios.post(`${API}/forms/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      return r.data?.path || null;
    } catch { return null; }
  };

  const onPickFile = async (e) => {
    setAttachError("");
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file
    for (const f of files) {
      const isImage = (f.type || "").startsWith("image/") || IMAGE_EXT_RE.test(f.name || "");
      if (!isImage) {
        setAttachError("Must be a picture file (PNG, JPG, GIF, WEBP, or SVG). Documents like Word or PDF can't be attached.");
        continue;
      }
      await uploadBlob(f, f.name || "photo", f.name || "Photo");
    }
  };

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!message.trim()) return;
    setState("sending");
    const resolvedArea = area || detectArea(location.pathname);
    const shotPath = await uploadShot();
    const paths = [...(shotPath ? [shotPath] : []), ...attachments.filter((a) => a.path).map((a) => a.path)];
    try {
      await axios.post(`${API}/forms/submit`, {
        form_type: "problem_report",
        first_name: user?.first_name || user?.name || "",
        email: email.trim(),
        subject: `Problem: ${resolvedArea}`,
        message: message.trim(),
        user_id: user?.id || "",
        blocking,
        attachments: paths,
        metadata: {
          area: resolvedArea,
          page_url: typeof window !== "undefined" ? window.location.href : location.pathname,
          path: location.pathname,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        },
      });
      setState("sent");
      setTimeout(() => setOpen(false), 2200);
    } catch {
      setState("error");
    }
  };

  const anyUploading = attachments.some((a) => a.uploading);

  return (
    <>
      {/* Unified "Report a Problem" pop-out tab — right edge, vertically
          centered, on EVERY page (games, Owl, everything). ~20% smaller than
          before. Tap its × to tuck it out of the way; the green magnet
          (lower-left) brings it — and every other helper — back. */}
      {!open && !(tucked && canTuck) && !(fabsCollapsed && isMobile) && (
        <div className="fixed z-[60] flex flex-col items-end" style={reportDrag.style} {...reportDrag.bind} data-testid="report-problem-wrap">
          {canTuck && (
            <button
              type="button"
              onClick={() => { if (reportDrag.shouldSwallowClick()) return; tuckReport(); }}
              className="w-4 h-4 -mb-1 mr-0.5 z-10 rounded-full bg-black/75 border border-white/30 flex items-center justify-center hover:bg-black active:scale-95 transition-all"
              data-testid="report-problem-tuck"
              aria-label="Tuck away Report a Problem"
              title="Tuck away — tap the green magnet to bring it back"
            >
              <X size={9} className="text-white/80" />
            </button>
          )}
          <button
            onClick={() => { if (reportDrag.shouldSwallowClick()) return; openWithCapture(); }}
            data-testid="report-problem-button"
            aria-label="Report a problem"
            title="Report a problem — drag to move me anywhere"
            className="flex flex-col items-center gap-1 rounded-l-xl bg-rose-600/95 hover:bg-rose-600 text-white shadow-lg shadow-rose-900/40 backdrop-blur transition-colors px-1.5 py-3.5 border border-r-0 border-rose-300/30 touch-none select-none"
          >
            <Flag size={13} className="flex-shrink-0" />
            <span className="text-[10px] font-fredoka font-extrabold tracking-tight leading-none" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>Report a Problem</span>
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-3 sm:p-4" data-testid="report-problem-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md rounded-3xl border border-white/12 bg-slate-900 p-5 sm:p-6 shadow-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <button onClick={() => setOpen(false)} data-testid="report-problem-close"
              className="absolute right-4 top-4 text-white/40 hover:text-white"><X size={18} /></button>

            {state === "sent" ? (
              <div className="text-center py-6" data-testid="report-problem-success">
                <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-3" />
                <div className="text-white font-fredoka font-extrabold text-lg mb-1">Thank you!</div>
                <p className="text-white/60 text-sm">Your report was sent to our team. We'll look into it and email you when it's fixed.</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-9 h-9 rounded-xl bg-rose-500/15 border border-rose-400/30 flex items-center justify-center"><AlertTriangle size={18} className="text-rose-300" /></div>
                  <div>
                    <div className="text-white font-fredoka font-extrabold text-base leading-tight">Report a Problem</div>
                    <div className="text-white/45 text-xs">Something not working? Tell us and we'll fix it.</div>
                  </div>
                </div>

                <label className="block mt-4 mb-1 text-white/60 text-xs font-bold uppercase tracking-wide">Where did it happen?</label>
                <input value={area || detectArea(location.pathname)} onChange={(e) => setArea(e.target.value)} data-testid="report-problem-area"
                  className="w-full rounded-xl bg-white/[0.06] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-rose-400/50" />
                <p className="text-white/30 text-[11px] mt-1 truncate" data-testid="report-problem-page">Page: {typeof window !== "undefined" ? window.location.pathname : location.pathname}</p>

                <label className="block mt-3 mb-1 text-white/60 text-xs font-bold uppercase tracking-wide">What happened?</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required
                  data-testid="report-problem-message"
                  placeholder="Describe what went wrong — for example, a game froze, a button didn't work, or something looked broken."
                  className="w-full rounded-xl bg-white/[0.06] border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-rose-400/50 resize-none" />

                {/* Picture attachments — auto screenshot (draw on it) + manual attach */}
                <label className="block mt-3 mb-1 text-white/60 text-xs font-bold uppercase tracking-wide">Pictures</label>
                {capturing && (
                  <div className="flex items-center gap-1.5 text-white/50 text-xs px-1 py-2 mb-1"><Camera size={13} /> <Loader2 size={13} className="animate-spin" /> capturing your screen…</div>
                )}
                {shotUrl && (
                  <div className="mb-2" data-testid="report-problem-annotate">
                    <div className="flex items-center justify-between flex-wrap gap-x-2 gap-y-1 mb-1">
                      <span className="text-white/50 text-[11px] flex items-center gap-1"><Pencil size={12} className="text-rose-300" /> Draw or circle what's wrong</span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={undoDraw} disabled={!canUndo} data-testid="report-problem-annotate-undo"
                          className="text-white/45 hover:text-white text-[11px] font-bold disabled:opacity-30 inline-flex items-center gap-1"><Undo2 size={11} /> Undo</button>
                        <button type="button" onClick={clearDrawing} disabled={!drewOnShot} data-testid="report-problem-annotate-clear"
                          className="text-white/45 hover:text-white text-[11px] font-bold disabled:opacity-30 inline-flex items-center gap-1"><Eraser size={11} /> Clear</button>
                        <button type="button" onClick={removeShot} data-testid="report-problem-annotate-remove"
                          className="text-white/45 hover:text-rose-300 text-[11px] font-bold inline-flex items-center gap-1"><Trash2 size={11} /> Remove</button>
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-1.5 mb-1.5" data-testid="report-problem-tools">
                      {[
                        { id: "pen", label: "Pen", Icon: Pencil },
                        { id: "highlighter", label: "Highlighter", Icon: Highlighter },
                        { id: "arrow", label: "Arrow", Icon: ArrowUpRight },
                      ].map(({ id, label, Icon }) => (
                        <button key={id} type="button" onClick={() => setTool(id)} data-testid={`report-problem-tool-${id}`}
                          title={label}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-all ${tool === id ? "bg-rose-500 text-white border-rose-400" : "bg-white/[0.05] border-white/12 text-white/60 hover:text-white hover:bg-white/[0.1]"}`}>
                          <Icon size={12} /> {label}
                        </button>
                      ))}
                    </div>
                    <canvas ref={canvasRef} data-testid="report-problem-canvas"
                      onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
                      onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
                      className="rounded-lg border border-white/15 bg-white/[0.03] cursor-crosshair touch-none w-full" style={{ touchAction: "none" }} />
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-2" data-testid="report-problem-attachments">
                  {attachments.map((a) => (
                    <div key={a.id} className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/15 bg-white/[0.05] flex items-center justify-center">
                      {a.uploading ? <Loader2 size={16} className="animate-spin text-white/50" />
                        : <img src={a.url} alt={a.label} className="w-full h-full object-cover" data-testid="report-problem-thumb" />}
                      {!a.uploading && (
                        <button type="button" onClick={() => removeAttachment(a.id)} data-testid="report-problem-remove-thumb"
                          className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white/80 hover:text-white"><Trash2 size={11} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="report-problem-attach"
                    className="w-16 h-16 rounded-lg border border-dashed border-white/20 bg-white/[0.03] hover:bg-white/[0.07] flex flex-col items-center justify-center text-white/50 text-[10px] gap-0.5">
                    <ImagePlus size={16} /> Add
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFile} className="hidden" data-testid="report-problem-file-input" />
                </div>
                {attachError && <p className="text-amber-300 text-[11px] mb-2" data-testid="report-problem-attach-error">{attachError}</p>}
                <p className="text-white/30 text-[10px] mb-1">We snap the screen for you. You can also add a photo — picture files only (PNG, JPG, GIF, WEBP, SVG).</p>

                <label className="block mt-3 mb-1 text-white/60 text-xs font-bold uppercase tracking-wide">Your email (optional)</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="report-problem-email"
                  placeholder="you@example.com"
                  className="w-full rounded-xl bg-white/[0.06] border border-white/15 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-rose-400/50" />
                <p className="text-white/30 text-[10px] mt-1">Add your email and we'll let you know the moment it's fixed.</p>

                <button type="button" onClick={() => setBlocking((b) => !b)} data-testid="report-problem-blocking"
                  className={`w-full mt-3 flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-all ${blocking ? "border-amber-400/60 bg-amber-500/15" : "border-white/12 bg-white/[0.04] hover:bg-white/[0.07]"}`}>
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${blocking ? "bg-amber-400" : "bg-white/10 border border-white/20"}`}>
                    {blocking ? <Zap size={13} className="text-amber-950" /> : <Zap size={13} className="text-white/40" />}
                  </span>
                  <span>
                    <span className="block text-white text-sm font-bold">This is blocking me</span>
                    <span className="block text-white/45 text-[11px]">Turn on if you can't keep using the app — we'll bump it to the top.</span>
                  </span>
                </button>

                {state === "error" && (
                  <p className="text-rose-300 text-xs mt-2" data-testid="report-problem-error">Couldn't send just now — please try again in a moment.</p>
                )}

                <button type="submit" disabled={state === "sending" || !message.trim() || anyUploading} data-testid="report-problem-submit"
                  className="w-full mt-4 py-3 rounded-xl bg-rose-500 hover:bg-rose-400 text-white font-fredoka font-extrabold text-sm disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                  {state === "sending" ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : (anyUploading ? <><Loader2 className="animate-spin" size={16} /> Attaching…</> : <><Send size={16} /> Send Report</>)}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
